/**
 * Settings → Communication → Outbound webhooks (G17).
 *
 * Vite SPA port of:
 *   frontend/src/app/(dashboard)/dashboard/settings/webhooks/page.tsx
 *   frontend/src/components/settings/webhook-form.tsx
 *   frontend/src/hooks/use-webhooks.ts
 *
 * HR-admins can configure outbound Slack / MS Teams webhook subscriptions.
 * The backend stores URLs encrypted and returns them masked so the plain
 * URL never appears after creation.
 *
 * Features:
 *   - List: label, provider badge, category, masked URL, active status,
 *     last-delivery status.
 *   - Add: CreateWebhookDialog (provider, URL, label, category, is_active).
 *   - Edit: label / category / is_active only (URL is immutable post-create).
 *   - Disable: toggle is_active via PATCH.
 *   - Delete: with an inline confirm AlertDialog.
 *   - Test fire: POST /{id}/test, shows inline result toast.
 *   - Delivery log: expandable panel per webhook (last 50, newest-first).
 *
 * Migration note (Wave F, 2026-06-05): direct port following Vite SPA
 * conventions — TanStack Router createFileRoute, useHrRoles() for the
 * HR-admin gate, SettingsShell + SettingsPageHeader wrapper.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  Loader2,
  Pencil,
  Plus,
  Send,
  Trash2,
  Webhook,
} from "lucide-react";
import { toast } from "sonner";

import { toApiError } from "@/lib/api";
import {
  listWebhooks,
  listWebhookDeliveries,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  type OutboundWebhook,
  type CreateWebhookRequest,
  type UpdateWebhookRequest,
} from "@/lib/api/webhooks";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { StatusBadge } from "@/components/shared/status-badge";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/webhooks",
)({
  component: SettingsWebhooksRoute,
});

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const webhookKeys = {
  all: ["webhooks"] as const,
  list: () => [...webhookKeys.all, "list"] as const,
  deliveries: (id: number) =>
    [...webhookKeys.all, "deliveries", id] as const,
};

function invalidateList(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: webhookKeys.list() });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useWebhooks() {
  return useQuery({
    queryKey: webhookKeys.list(),
    queryFn: ({ signal }) => listWebhooks(signal),
    staleTime: 30 * 1000,
  });
}

function useWebhookDeliveriesQuery(id: number | null) {
  return useQuery({
    queryKey: webhookKeys.deliveries(id ?? 0),
    queryFn: ({ signal }) => listWebhookDeliveries(id!, signal),
    enabled: id != null,
    staleTime: 10 * 1000,
  });
}

function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWebhookRequest) => createWebhook(body),
    onSuccess: () => invalidateList(qc),
  });
}

function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateWebhookRequest }) =>
      updateWebhook(vars.id, vars.body),
    onSuccess: () => invalidateList(qc),
  });
}

function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteWebhook(id),
    onSuccess: () => invalidateList(qc),
  });
}

function useTestWebhook() {
  return useMutation({
    mutationFn: (id: number) => testWebhook(id),
  });
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_OPTIONS = [
  { value: "*", label: "All notifications" },
  { value: "leave_approved", label: "Leave approved" },
  { value: "leave_rejected", label: "Leave rejected" },
  { value: "leave_submitted", label: "Leave submitted" },
  { value: "expense_approved", label: "Expense approved" },
  { value: "expense_rejected", label: "Expense rejected" },
  { value: "posh_complaint_submitted", label: "POSH complaint submitted" },
  { value: "onboarding_task_due", label: "Onboarding task due" },
  { value: "document_expiry", label: "Document expiry" },
  { value: "payroll_locked", label: "Payroll run locked" },
  { value: "offboarding_initiated", label: "Offboarding initiated" },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function providerLabel(p: string) {
  return p === "teams" ? "MS Teams" : "Slack";
}

function categoryLabel(c: string) {
  if (c === "*") return "All notifications";
  return c.replaceAll("_", " ");
}

function deliveryStatusTone(
  status: number | null | undefined,
): "success" | "danger" | "neutral" {
  if (status == null) return "neutral";
  if (status >= 200 && status < 300) return "success";
  return "danger";
}

// ---------------------------------------------------------------------------
// Form schemas
// ---------------------------------------------------------------------------

const createSchema = z.object({
  provider: z.enum(["slack", "teams"]),
  url: z
    .string()
    .min(1, "Webhook URL is required")
    .regex(/^https:\/\/.+/, "Must be a valid HTTPS URL"),
  label: z
    .string()
    .min(1, "Label is required")
    .max(100, "Label must be 100 characters or fewer"),
  category: z.string().min(1, "Category is required"),
  is_active: z.boolean(),
});

const editSchema = z.object({
  label: z
    .string()
    .min(1, "Label is required")
    .max(100, "Label must be 100 characters or fewer"),
  category: z.string().min(1, "Category is required"),
  is_active: z.boolean(),
});

type CreateFormValues = z.infer<typeof createSchema>;
type EditFormValues = z.infer<typeof editSchema>;

// ---------------------------------------------------------------------------
// DeliveryLog — expandable per-webhook panel
// ---------------------------------------------------------------------------

function DeliveryLog({ webhookId }: { readonly webhookId: number }) {
  const q = useWebhookDeliveriesQuery(webhookId);
  const rows = q.data?.items ?? [];

  if (q.isLoading) {
    return (
      <div className="space-y-1 px-4 pb-4">
        {["d1", "d2", "d3"].map((k) => (
          <Skeleton key={k} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (q.isError) {
    return (
      <p className="px-4 pb-4 text-sm text-destructive">
        {toApiError(q.error).message}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="px-4 pb-4 text-sm text-muted-foreground">
        No deliveries recorded yet.
      </p>
    );
  }

  return (
    <ul
      className="divide-y divide-border px-4 pb-4"
      data-testid={`webhook-deliveries-${webhookId}`}
    >
      {rows.map((d) => (
        <li
          key={d.id}
          className="flex flex-wrap items-start justify-between gap-2 py-2 text-xs"
        >
          <div className="min-w-0">
            <span className="font-medium">{d.notif_category}</span>
            <span className="ml-2 text-muted-foreground">
              {d.payload_summary}
            </span>
            {d.error && (
              <p className="mt-0.5 text-destructive">{d.error}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <StatusBadge
              tone={deliveryStatusTone(d.status_code)}
              label={d.status_code == null ? "—" : String(d.status_code)}
            />
            <span className="text-muted-foreground">
              {new Date(d.sent_at).toLocaleString()}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// CreateWebhookDialog
// ---------------------------------------------------------------------------

function CreateWebhookDialog() {
  const [open, setOpen] = useState(false);
  const createMut = useCreateWebhook();

  const form = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      provider: "slack",
      url: "",
      label: "",
      category: "*",
      is_active: true,
    },
  });

  const pending = createMut.isPending;

  async function onSubmit(values: CreateFormValues) {
    try {
      await createMut.mutateAsync({
        provider: values.provider,
        url: values.url.trim(),
        label: values.label.trim(),
        category: values.category,
        is_active: values.is_active,
      });
      toast.success(`Webhook "${values.label.trim()}" created.`);
      setOpen(false);
      form.reset();
    } catch (err) {
      const e = toApiError(err);
      if (e.field && e.field in form.getValues()) {
        form.setError(e.field as keyof CreateFormValues, {
          message: e.message,
        });
      } else {
        toast.error(e.message);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-1.5" data-testid="webhook-add-btn">
          <Plus className="h-4 w-4" />
          Add webhook
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New outbound webhook</DialogTitle>
          <DialogDescription>
            WorkRight will POST a JSON payload to this URL when the selected
            notification fires. Use Slack incoming webhooks or an MS Teams
            connector URL.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger data-testid="webhook-provider-select">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="slack">Slack</SelectItem>
                      <SelectItem value="teams">Microsoft Teams</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Webhook URL *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://hooks.slack.com/services/..."
                      autoComplete="off"
                      data-testid="webhook-url-input"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Slack: copy from &quot;Incoming Webhooks&quot; app
                    integration. Teams: use the &quot;Incoming Webhook&quot;
                    connector URL. The URL is stored encrypted and only shown
                    masked after creation.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. #hr-alerts"
                      data-testid="webhook-label-input"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Shown in the webhook list and in test-fire messages.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notification type *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger data-testid="webhook-category-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    &quot;All notifications&quot; fires for every event type.
                    Pick a specific type to filter.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem>
                  <label className="flex items-start gap-3 text-sm">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        name={field.name}
                        data-testid="webhook-active-checkbox"
                      />
                    </FormControl>
                    <span>
                      <span className="font-medium">Active</span>
                      <span className="block text-muted-foreground">
                        Inactive webhooks are skipped during dispatch.
                      </span>
                    </span>
                  </label>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={pending}
                className="gap-1.5"
                data-testid="webhook-submit-btn"
              >
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// EditWebhookDialog
// ---------------------------------------------------------------------------

function EditWebhookDialog({ webhook }: { readonly webhook: OutboundWebhook }) {
  const [open, setOpen] = useState(false);
  const updateMut = useUpdateWebhook();

  const form = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      label: webhook.label,
      category: webhook.category,
      is_active: webhook.is_active,
    },
  });

  function handleOpenChange(v: boolean) {
    setOpen(v);
    if (v) {
      form.reset({
        label: webhook.label,
        category: webhook.category,
        is_active: webhook.is_active,
      });
    }
  }

  const pending = updateMut.isPending;

  async function onSubmit(values: EditFormValues) {
    try {
      await updateMut.mutateAsync({
        id: webhook.id,
        body: {
          label: values.label.trim(),
          category: values.category,
          is_active: values.is_active,
        },
      });
      toast.success(`Webhook "${values.label.trim()}" updated.`);
      setOpen(false);
    } catch (err) {
      const e = toApiError(err);
      if (e.field && e.field in form.getValues()) {
        form.setError(e.field as keyof EditFormValues, { message: e.message });
      } else {
        toast.error(e.message);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          data-testid={`webhook-edit-btn-${webhook.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit webhook</DialogTitle>
          <DialogDescription>
            Update the label, notification type, or active state. To change
            the URL, delete this webhook and create a new one.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">URL: </span>
              {webhook.url_masked}
            </div>
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notification type *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem>
                  <label className="flex items-start gap-3 text-sm">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        name={field.name}
                      />
                    </FormControl>
                    <span>
                      <span className="font-medium">Active</span>
                      <span className="block text-muted-foreground">
                        Inactive webhooks are skipped during dispatch.
                      </span>
                    </span>
                  </label>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={pending} className="gap-1.5">
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// WebhookRow — single webhook card
// ---------------------------------------------------------------------------

function WebhookRow({
  webhook,
  isHrAdmin,
}: {
  readonly webhook: OutboundWebhook;
  readonly isHrAdmin: boolean;
}) {
  const [showLog, setShowLog] = useState(false);
  const deleteMut = useDeleteWebhook();
  const updateMut = useUpdateWebhook();
  const testMut = useTestWebhook();

  const busy =
    deleteMut.isPending || updateMut.isPending || testMut.isPending;

  async function handleToggleActive() {
    try {
      await updateMut.mutateAsync({
        id: webhook.id,
        body: { is_active: !webhook.is_active },
      });
      toast.success(
        webhook.is_active
          ? `Webhook "${webhook.label}" disabled.`
          : `Webhook "${webhook.label}" enabled.`,
      );
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function handleDelete() {
    try {
      await deleteMut.mutateAsync(webhook.id);
      toast.success(`Webhook "${webhook.label}" deleted.`);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function handleTest() {
    try {
      const res = await testMut.mutateAsync(webhook.id);
      if (res.status_code >= 200 && res.status_code < 300) {
        toast.success(
          `Test fired — ${webhook.label} responded ${res.status_code}.`,
        );
      } else {
        const detail = res.error ? `: ${res.error}` : "";
        toast.error(`Test failed (${res.status_code})${detail}.`);
      }
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const lastStatusTone = deliveryStatusTone(webhook.last_status);

  return (
    <Card data-testid={`webhook-row-${webhook.id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <Send className="h-4 w-4 shrink-0 text-primary" />
              {webhook.label}
            </CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <StatusBadge
                tone="info"
                label={providerLabel(webhook.provider)}
              />
              <span className="text-muted-foreground">
                {categoryLabel(webhook.category)}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {webhook.url_masked}
              </span>
              {webhook.is_active ? (
                <StatusBadge tone="success" label="Active" />
              ) : (
                <StatusBadge tone="neutral" label="Inactive" />
              )}
              {webhook.last_status != null && (
                <StatusBadge
                  tone={lastStatusTone}
                  label={`Last: ${webhook.last_status}`}
                />
              )}
            </div>
          </div>
          {isHrAdmin && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => void handleTest()}
                disabled={busy || !webhook.is_active}
                title={
                  webhook.is_active
                    ? "Send a test event"
                    : "Enable the webhook to test"
                }
                data-testid={`webhook-test-btn-${webhook.id}`}
              >
                {testMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FlaskConical className="h-3.5 w-3.5" />
                )}
                Test
              </Button>
              <EditWebhookDialog webhook={webhook} />
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => void handleToggleActive()}
                disabled={busy}
                data-testid={`webhook-toggle-btn-${webhook.id}`}
              >
                {updateMut.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                {!updateMut.isPending && webhook.is_active && (
                  <AlertTriangle className="h-3.5 w-3.5" />
                )}
                {!updateMut.isPending && !webhook.is_active && (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                {webhook.is_active ? "Disable" : "Enable"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1.5"
                    disabled={busy}
                    data-testid={`webhook-delete-btn-${webhook.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete webhook?</AlertDialogTitle>
                    <AlertDialogDescription>
                      &quot;{webhook.label}&quot; ({webhook.url_masked}) will
                      be permanently deleted along with its delivery history.
                      This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => void handleDelete()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteMut.isPending ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : null}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pb-0">
        <button
          type="button"
          className="flex w-full items-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setShowLog((v) => !v)}
          data-testid={`webhook-log-toggle-${webhook.id}`}
        >
          {showLog ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          {showLog ? "Hide" : "Show"} delivery log
        </button>
        {showLog && <DeliveryLog webhookId={webhook.id} />}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function SettingsWebhooksRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useWebhooks();
  const rows = q.data?.items ?? [];

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Outbound webhooks</h2>
              <p className="text-sm text-muted-foreground">
                Push WorkRight notifications to Slack or Microsoft Teams.
                Webhook URLs are stored encrypted and only shown masked after
                creation.
              </p>
            </div>
            {isHrAdmin && <CreateWebhookDialog />}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can manage webhook
                subscriptions.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["w1", "w2", "w3"].map((k) => (
                <Skeleton key={k} className="h-24 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load webhooks</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && rows.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                  <Webhook className="h-6 w-6 text-muted-foreground" />
                </span>
                <div className="space-y-1">
                  <p className="text-base font-medium">No webhooks yet</p>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    {isHrAdmin
                      ? "Add a webhook above to start receiving WorkRight events in Slack or Teams."
                      : "Ask an HR admin to configure outbound webhooks."}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {q.data && rows.length > 0 && (
            <div className="space-y-3" data-testid="webhooks-list">
              {rows.map((w) => (
                <WebhookRow key={w.id} webhook={w} isHrAdmin={isHrAdmin} />
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
